import { type IntlShape, useIntl } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { getProductForTranslation } from '../services/get-product-translation-key';
import type { ExperimentCohort, ExperimentCohortControlVariation, Product } from '../types';

import {
	EditionAwarenessContextType,
	useEditionAwareness,
	useSiteInformation,
} from './edition-awareness-context';
import { isEligibleForEduDiscount } from './is-eligible-for-edu-discount';
import { isEligibleForStandardToPremium } from './is-eligible-for-standard-to-premium';
import { isEligibleForStandardTrialsUserAndStorageLimitBreakers } from './is-eligible-for-standard-trials-user-and-storage-limit-breakers';
import { isEligibleForTrialCountdown } from './is-eligible-for-trial-countdown';

// TODO: `licensedUserLimit` should be fetched from the server
const PRODUCT_LIMITS_MAP: Record<Product, { licensedUserLimit: number; warningUserLimit: number }> =
	{
		'jira-product-discovery': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
		jira: {
			licensedUserLimit: 10,
			warningUserLimit: 7,
		},
		confluence: {
			licensedUserLimit: 10,
			warningUserLimit: 3,
		},
		'jira-service-management': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
	};

export type IconType = 'gem' | 'warning' | 'credit-card';
export type Appearance = 'default' | 'warning' | 'danger';

export type ButtonContent = {
	text: string;
	appearance: 'default' | 'warning' | 'danger';
	icon: IconType;
	isDismissible?: boolean;
	isEducationDiscount?: boolean;
};

function getFreeContent({
	context,
	formatMessage,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
}): ButtonContent {
	const { edition, config, licensedUsers, product } = context;

	const limits = PRODUCT_LIMITS_MAP[product];

	// Education offer has a higher priority than all the other messages
	if (
		isEligibleForEduDiscount(context) &&
		FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'offer_edu_org_discount',
			'cohort',
			'not-enrolled',
		) === 'variation'
	) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.education-offer',
				defaultMessage: 'Education discount',
				description:
					'Informs the user that they may be are eligible for an education offer which is a discount on all products',
			}),
			appearance: 'default',
			icon: 'gem',
			isEducationDiscount: true,
		};
	}

	if (config.warnOnUserLimit && licensedUsers) {
		if (licensedUsers >= limits.licensedUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.jpd.no-creators-left',
						defaultMessage:
							'No {product, select, jpd {creators} jsm {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are no licensed users left and they may need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: product !== 'confluence' ? 'danger' : 'warning',
				icon: 'warning',
			};
		}

		if (licensedUsers >= limits.warningUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.jpd.limited-creators-left',
						defaultMessage:
							'Limited {product, select, jpd {creators} jsm {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are limited licensed users left before they need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (product === 'confluence' && edition === 'free' && licensedUsers && licensedUsers >= 3) {
		// Confluence has its own variety when compared to the `userLimits` feature
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.upgrade',
				defaultMessage: 'Upgrade',
				description: 'Informs the user that they can upgrade their product',
			}),
			appearance: 'warning',
			icon: 'warning',
		};
	}

	return {
		text: formatMessage({
			id: '@atlassian/edition-awareness.button.upgrade',
			defaultMessage: 'Upgrade',
			description: 'Informs the user that they can upgrade their product',
		}),
		appearance: 'default',
		icon: 'gem',
	};
}

function getTrialContent({
	storageUsageInGB,
	context,
	formatMessage,
}: {
	storageUsageInGB?: number;
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
}): ButtonContent {
	const { product, edition, trialDaysLeft, trialLengthDays } = context;

	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'gem';

	if (
		product === 'jira' &&
		isEligibleForStandardTrialsUserAndStorageLimitBreakers({
			context,
			storageUsageInGB,
		}) &&
		FeatureGates.getExperimentValue<ExperimentCohort>(
			'jira_edition_awareness_standard_trials',
			'cohort',
			'not-enrolled',
		) === 'treatment'
	) {
		return {
			text: formatMessage(
				{
					id: '@atlassian/edition-awareness.button.daysLeft',
					defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
					description: 'Informs the user how many days they have left in trial',
				},
				{ trialDaysLeft },
			),
			appearance: trialDaysLeft && trialDaysLeft <= 3 ? 'danger' : 'warning',
			icon: 'warning',
		};
	}

	if (
		product === 'confluence' &&
		isEligibleForStandardTrialsUserAndStorageLimitBreakers({
			context,
			storageUsageInGB,
		}) &&
		FeatureGates.getExperimentValue<ExperimentCohort>(
			'connie_edition_awareness_standard_trials',
			'cohort',
			'not-enrolled',
		) === 'treatment'
	) {
		return {
			text: formatMessage(
				{
					id: '@atlassian/edition-awareness.button.daysLeft',
					defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
					description: 'Informs the user how many days they have left in trial',
				},
				{ trialDaysLeft },
			),
			appearance: 'warning',
			icon: 'warning',
		};
	}

	if (['jira', 'jira-service-management', 'jira-product-discovery'].includes(product)) {
		// Trial Countdown Experiment
		const trialCountdownExperimentEnabled =
			isEligibleForTrialCountdown(context) &&
			FeatureGates.getExperimentValue<boolean>(
				'jira_edition_awareness_trial_countdown',
				'isEnabled',
				false,
			);

		// If trial is 3 days or less, swap the button to the warning state
		if (trialCountdownExperimentEnabled && !!trialDaysLeft && trialDaysLeft <= 3) {
			appearance = 'warning';
			icon = 'warning';
		}

		// If trial is still in its first half, show trial message instead of trial days left
		const isInFirstHalfOfTrial = Boolean(
			trialCountdownExperimentEnabled &&
				trialDaysLeft &&
				trialLengthDays &&
				trialDaysLeft > Math.ceil(trialLengthDays / 2),
		);

		if (isInFirstHalfOfTrial) {
			if (edition === 'premium') {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.premium-trial',
						defaultMessage: 'Premium trial',
						description: 'Informs the user that they are on a premium trial',
					}),
					appearance,
					icon,
				};
			}

			if (edition === 'standard') {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.standard-trial',
						defaultMessage: 'Standard trial',
						description: 'Informs the user that they are on a standard trial',
					}),
					appearance,
					icon,
				};
			}
		}
	}

	return {
		text: formatMessage(
			{
				id: '@atlassian/edition-awareness.button.daysLeft',
				defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
				description: 'Informs the user how many days they have left in trial',
			},
			{ trialDaysLeft },
		),
		appearance,
		icon,
	};
}

function getPredunningContent({
	storageUsageInGB,
	context,
	formatMessage,
}: {
	storageUsageInGB?: number;
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
}): ButtonContent {
	const { product } = context;

	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'credit-card';

	if (
		isEligibleForStandardTrialsUserAndStorageLimitBreakers({
			context,
			storageUsageInGB,
		}) &&
		FeatureGates.getExperimentValue<ExperimentCohort>(
			product === 'jira'
				? 'jira_edition_awareness_standard_trials'
				: 'connie_edition_awareness_standard_trials',
			'cohort',
			'not-enrolled',
		) === 'treatment'
	) {
		appearance = 'warning';
		icon = 'warning';
	}

	return {
		text: formatMessage({
			id: '@atlassian/edition-awareness.button.addPaymentDetails',
			defaultMessage: 'Add payment details',
			description: 'Informs the user how many days they have left in trial',
		}),
		appearance,
		icon,
	};
}

export function useButtonContent(): ButtonContent | null {
	const { formatMessage } = useIntl();

	const context = useEditionAwareness();
	const { edition, isInPredunning, isInTrial } = context;
	const { storageUsageInGB } = useSiteInformation();

	if (edition === 'free') {
		return getFreeContent({ context, formatMessage });
	}

	if (isInTrial) {
		return getTrialContent({ storageUsageInGB, context, formatMessage });
	}

	if (isInPredunning) {
		return getPredunningContent({ storageUsageInGB, context, formatMessage });
	}

	if (isEligibleForStandardToPremium(context)) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.upgrade',
				defaultMessage: 'Upgrade',
				description: 'Informs the user that they can upgrade their product',
			}),
			appearance: 'default',
			icon: 'gem',
			isDismissible: true,
		};
	}

	return null;
}
